import React, { FunctionComponent, ReactElement, useEffect, useState } from "react";
import logo from "../../assets/images/logo.png";
import useClaimsToast from "../../hooks/useToast";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { Alert, AlertIcon, Button, Flex, FormControl, Heading, Input } from "@chakra-ui/react";
import { MainPageCard } from "../../shared/layout/index.styles";
import { postResetPassword } from "../../utils/api/auth";
import { ResetPasswordResponse } from "../../utils/api/protos/messages/auth.message_pb";
import { RouterPathMapping } from "../../Router";
import { isPasswordValid } from "../../utils/helpers";

interface PasswordResetProps {}

const PasswordReset: FunctionComponent<PasswordResetProps> = (props: PasswordResetProps): ReactElement => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const email = queryParams.get("email");
	const token = queryParams.get("token");
	const isSetup = queryParams.get("setup") === "true";
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [newPassword, setNewPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");
	const [doPasswordsMatch, setDoPasswordsMatch] = useState<boolean>(false);

	const toast = useClaimsToast();
	const navigate = useNavigate();
	const { getUser } = useAuth();
	const user = getUser();

	const resetInputs = (): void => {
		setNewPassword("");
		setConfirmPassword("");
	};

	const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
		if (event.key.toLowerCase() === "enter") handlePasswordReset();
	};

	const handlePasswordReset = async (): Promise<void> => {
		if (!doPasswordsMatch) {
			toast("Error", "Both Passwords must match.", "error");
			return;
		}
		if (!isPasswordValid(newPassword) || !isPasswordValid(confirmPassword)) {
			toast("Error", "Your password must be at least 8 characters long and no more than 64 characters.", "error");
			return;
		}
		if (!email || email.length === 0) return;
		if (!token || token.length === 0) return;
		setIsLoading(true);

		postResetPassword(email, newPassword, token)
			.then((response: ResetPasswordResponse) => {
				toast(
					"Success",
					"Your password has been successfully changed. Please login using your new password.",
					"success"
				);
				navigate(RouterPathMapping.Login);
			})
			.catch((err) => {
				toast("Error", err, "error");
			})
			.finally(() => {
				resetInputs();
				setIsLoading(false);
			});
	};

	useEffect(() => {
		setDoPasswordsMatch(newPassword === confirmPassword);
	}, [newPassword, confirmPassword]);

	useEffect(() => {
		if (user) navigate(-1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	return (
		<Flex flexDirection="column" justifyContent="center" alignItems="center" p={7} width="100%">
			<img src={logo} alt="logo" />
			<MainPageCard mt={6}>
				<Heading mb="5" textAlign="center" color="secondary">
					{isSetup ? "Set" : "Reset"} Password
				</Heading>
				<Flex flexDirection="column" alignItems="center">
					<FormControl maxWidth={400}>
						<Input
							variant="mainInput"
							id="password"
							data-testid="password-reset-password-input"
							type="password"
							placeholder="Password"
							value={newPassword}
							disabled={isLoading}
							onKeyDown={(e) => handleInputKeyDown(e)}
							onChange={(e) => setNewPassword(e.target.value)}
						/>
					</FormControl>
					<FormControl maxWidth={400}>
						<Input
							variant="mainInput"
							id="password-confirm"
							data-testid="password-reset-password-confirm-input"
							type="password"
							placeholder="Confirm Password"
							value={confirmPassword}
							disabled={isLoading}
							onKeyDown={(e) => handleInputKeyDown(e)}
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</FormControl>
					<Button
						type="submit"
						data-testid="submit-password-reset"
						colorScheme="orange"
						minWidth={200}
						mt={5}
						isLoading={isLoading}
						onClick={() => handlePasswordReset()}>
						Submit
					</Button>
					<Alert status="info" marginTop="3%">
						<AlertIcon />
						<ul style={{ marginLeft: "5%" }}>
							<li>
								<b>Your password should be at least 8 characters long.</b>
							</li>
							<li>
								<b>You cannot re-use a previous password.</b>
							</li>
							<li>Avoid common words, predictable patterns, and personal information.</li>
							<li>
								Consider using a Pass Phrase, such as <i>CorrectBatteryHorseStaple</i>.
							</li>
						</ul>
					</Alert>
				</Flex>
			</MainPageCard>
		</Flex>
	);
};

export default PasswordReset;
